import {UserAuth, UserAuthItem, UserAuthItemTypeEnum} from "arteco-api-client-ts";
import {NavigateFunction, useNavigate} from "react-router-dom";
import {ReduxState, updateAuthRedux, useAppDispatch, useAppSelector} from "../../store";
import {envVars} from "./env_vars";


const AUTH_LOCAL_STORE_KEY = "auth";

// Cómo entró la última vez (#14004). El botón pulsado queda pendiente y solo se recuerda cuando el
// login acaba bien: si se recordara al pulsar, una cuenta que el SSO de Workspace rechaza (un Gmail
// personal) volvería al bloqueo de Google cada vez, sin ver «Otra cuenta». «login_via» es la clave
// antigua, que se recordaba al pulsar: se descarta.
export const LOGIN_VIA_KEY = "login_via_ok";
export const LOGIN_VIA_PENDIENTE_KEY = "login_via_pendiente";
const LOGIN_VIA_ANTIGUA_KEY = "login_via";
type AppStateDispatcher = (newState: AuthState) => void;

export type AuthState = {
	user: UserAuth | undefined;
	selectedAuthority: UserAuthItem | undefined;
}

export interface RoleInfo {
    title: string;
    description: string;
    target: string;
    isAdmin: boolean;
}

export const ROLE_MAP = new Map<UserAuthItemTypeEnum, RoleInfo>();

ROLE_MAP.set(UserAuthItemTypeEnum.ADMIN_BILLING, {
    title: "FACTURACIÓN",
    description: "Módulo de facturación de la empresa",
    target: "/app/admin/billing",
    isAdmin: true
} as RoleInfo);

ROLE_MAP.set(UserAuthItemTypeEnum.ADMIN_EMPLOYEE, {
    title: "RRHH",
    description: "Módulo de gestión de recursos humanos",
    target: "/app/admin/employee",
    isAdmin: true
} as RoleInfo);

ROLE_MAP.set(UserAuthItemTypeEnum.ADMIN_RECRUITMENT, {
    title: "SELECCIÓN",
    description: "Módulo de gestión de los procesos de selección",
    target: "/app/admin/recruitment",
    isAdmin: true
} as RoleInfo);

ROLE_MAP.set(UserAuthItemTypeEnum.ADMIN_ACCOUNTING, {
    title: "CONTABILIDAD",
    description: "Módulo de contabilidad y gestión fiscal",
    target: "/app/admin/accounting",
    isAdmin: true
} as RoleInfo);

ROLE_MAP.set(UserAuthItemTypeEnum.ADMIN_MARKETING, {
    title: "MARKETING",
    description: "Módulo de marketing y CRM",
    target: "/app/admin/marketing",
    isAdmin: true
} as RoleInfo);

ROLE_MAP.set(UserAuthItemTypeEnum.ADMIN_COMPLIANCE, {
    title: "COMPLIANCE",
    description: "Sistema de gestión: indicadores, no conformidades, accesos, documentos y valoración de clientes",
    target: "/app/admin/compliance",
    isAdmin: true
} as RoleInfo);

ROLE_MAP.set(UserAuthItemTypeEnum.ADMIN, {
    title: "CONFIGURACIÓN",
    description: "Parámetros, usuarios y permisos, IA, plantillas y wiki del sistema",
    target: "/app/admin/config",
    isAdmin: true
} as RoleInfo);

ROLE_MAP.set(UserAuthItemTypeEnum.EMPLOYEE, {
    title: "EMPLEADO",
    description: "Accede al Portal del Empleado",
    target: "/app/employee"
} as RoleInfo);

ROLE_MAP.set(UserAuthItemTypeEnum.PUBLIC, {
    title: "PUBLICO",
    description: "Accede a la sección de usuarios generales",
    target: "/app/user/profile"
} as RoleInfo);

export const tryGoToDefaultPage = (auth: UserAuthItem | undefined, authManager: AuthManager, navigate : NavigateFunction) => {
    if (auth && auth.type) {
        const info = ROLE_MAP.get(auth.type);
        if (info && info.target) {
            authManager.saveSelectedAuthority(auth);
            navigate(info.target);
        }
    }
}

function stateSaveLocally(state: AuthState) {
	try {
		localStorage.setItem(AUTH_LOCAL_STORE_KEY, JSON.stringify(state));
	} catch {
		// localStorage blocked (Firefox strict tracking protection, private mode)
	}
}

function stateLoadLocally(): AuthState | undefined {
	try {
		const stateStr = localStorage.getItem(AUTH_LOCAL_STORE_KEY);
		if (stateStr) {
			return JSON.parse(stateStr) as AuthState;
		}
	} catch (e) {
		// NS_ERROR_FAILURE in Firefox when localStorage is blocked (private mode, strict tracking protection)
	}
	return undefined;
}

export function randomString(length: number) {
	let result = '';
	const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const charactersLength = characters.length;
	let counter = 0;
	while (counter < length) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
		counter += 1;
	}
	return result;
}

function stateInitial(): AuthState {
	return stateLoadLocally() || {} as AuthState;
}

export const AUTH_STATE_INITIAL: AuthState = stateInitial();


export function useAuthManager(): AuthManager {
	const navigate = useNavigate();
	const state = useAppSelector((state: ReduxState) => state.auth)
	const dispatch = useAppDispatch()
	if (!state) throw "No appState in userContext. Consider inject UserContextProvider";
	if (!dispatch) throw "No dispatch in userContext. Consider inject UserContextProvider";
	return new SimpleAuthManager(navigate, state,
		(newState) => {
			dispatch(updateAuthRedux(newState))
		});
}

export interface AuthManager {
	getAuthorities(): UserAuthItem[];

	saveSelectedAuthority(auth: UserAuthItem): AuthState;

	getSelectedAuthority(): UserAuthItem | undefined;

	saveUser(user: UserAuth): AuthState;

	tryRecoverState(): AuthState | undefined;

	doLogout(): void;

	isLogged(): boolean;

	getUsername(): string | undefined;

	getEmail(): string | undefined;

    getImageSrc(): string | undefined;


	isManager(): boolean;

	hasBudget(): boolean;

	getUserId(): number | undefined;

	clear(): void;
}


class SimpleAuthManager implements AuthManager {
	private readonly navigate: NavigateFunction;
	private readonly dispatch: AppStateDispatcher;
	private appState: AuthState; /* non null */

	constructor(navigate: NavigateFunction, appState: AuthState, dispatch: AppStateDispatcher) {
		this.navigate = navigate;
		this.appState = appState;
		this.dispatch = dispatch;
	}

	isLogged(): boolean {
		return this.appState.user?.email != undefined;
	}

	getUsername(): string | undefined {
		return this.appState.user?.username;
	}

	getEmail(): string | undefined {
		return this.appState.user?.email;
	}

    getImageSrc(): string | undefined {
        // Sin persona o sin foto da 404 y el avatar se queda con la silueta (#14236). El usuario va en la URL para
        // que «Ver como…» no reutilice la foto de otro desde la caché.
        const username = this.appState.user?.username;
        return username ? envVars.apiBasePath + "/api/user/photo?width=80&u=" + encodeURIComponent(username) : undefined;
    }

	getAuthorities(): UserAuthItem[] {
		return this.appState.user?.authorities || [];
	}

	getSelectedAuthority(): UserAuthItem | undefined {
		return this.appState.selectedAuthority;
	}


	isManager() {
		return this.appState.user?.manager || false;
	}

	hasBudget() {
		return this.appState.user?.budgets || false;
	}

	doLogout(): void {
		this.dispatch(AUTH_STATE_INITIAL);
		try {
			localStorage.removeItem(AUTH_LOCAL_STORE_KEY);
			localStorage.removeItem(LOGIN_VIA_KEY);
			localStorage.removeItem(LOGIN_VIA_PENDIENTE_KEY);
			localStorage.removeItem(LOGIN_VIA_ANTIGUA_KEY);
		} catch {}
		window.location.href = envVars.apiBasePath + "/logout";
	}

	getUserId(): number | undefined {
		return this.appState.user?.id;
	}

	clear(): void {
		this.dispatch({} as AuthState);
		try { localStorage.removeItem(AUTH_LOCAL_STORE_KEY); } catch {}
	}

	saveSelectedAuthority(auth: UserAuthItem): AuthState {
		const newState = {
			...this.appState,
			selectedAuthority: auth,
		} as AuthState;
		return this.modifyState(newState);
	}

	saveUser(user: UserAuth): AuthState {
		if (!user.username || !user.email || !user.authorities) {
			throw new Error("No user info retrieved");
		}
		try {
			const via = localStorage.getItem(LOGIN_VIA_PENDIENTE_KEY);
			if (via) {
				localStorage.setItem(LOGIN_VIA_KEY, via);
				localStorage.removeItem(LOGIN_VIA_PENDIENTE_KEY);
			}
		} catch {}
		const newState = {
			...this.appState,
			user: user
		} as AuthState;
		return this.modifyState(newState);
	}

	tryRecoverState(): AuthState | undefined {
		const newState = stateLoadLocally();
		if (newState) {
			return this.modifyState(newState);
		}
		return undefined;
	}

	modifyState(state: AuthState): AuthState {
		this.appState = state;
		this.dispatch(state);
		stateSaveLocally(state);
		return state;
	}
}

/**
 * Si el usuario tiene alguno de esos roles. Solo para no pedir lo que el API le va a negar (#14079):
 * un 403 en pantalla parece un fallo, y en un desplegable vaciaba el campo. Quien manda es el API.
 */
export const useHasAnyRole = (...roles: UserAuthItemTypeEnum[]): boolean => {
    const authManager = useAuthManager();
    const types = authManager.getAuthorities().map(a => a.type as string);
    return roles.some(r => types.includes(r));
};
